/**
 * POSTAL CODE DRAWER - Postal code drawer component
 * 
 * This file contains only the postal code drawer functionality:
 * - PostcodeDrawerComponent class
 * - Postal code validation and submission
 * - Error handling and styling
 */
import { DeliveryEvents } from '@theme/delivery-core';
import { updateDeliveryDisplay } from '@theme/delivery-display';
import { DeliveryCartAttributes } from '@theme/delivery-cart-attributes';
import { Component } from '@theme/component';

// Constants
const POSTCODE_FORMAT = {
  DIGITS_LENGTH: 4,
  LETTERS_LENGTH: 2
};

const DATASET_KEYS = {
  AUTO_SUBMISSION: 'isAutoSubmission',
  MANUAL_SUBMISSION: 'isManualSubmission',
  JUST_SUBMITTED: 'justSubmitted',
  ORIGINAL_BUTTON_TEXT: 'originalButtonText',
  ERROR_BUTTON_TEXT: 'errorButtonText'
};

const DEFAULT_TEXTS = {
  ERROR_BUTTON: 'Afsluiten zonder bezorgmoment',
  ORIGINAL_BUTTON: 'Verder naar bezorgmomenten',
  VALIDATION_ERROR: 'Please enter a valid Dutch postcode (e.g., 1234 AB)',
  UPDATE_ERROR: 'Error updating postcode. Please try again.'
};

const CONSOLE_PREFIX = '[POSTAL-CODE-DRAWER]';

// Helper Functions
/**
 * @param {string} level - Log level (log, warn, error)
 * @param {string} message - Message to log
 * @param {...any} args - Additional arguments
 */
function logMessage(level, message, ...args) {
  // /** @type {any} */ (console)[level](`🔍 ${CONSOLE_PREFIX} ${message}`, ...args);
}

/**
 * Postal code drawer component
 */
/**
 * A custom element that manages the collection footer.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} closeButton - The next button element.
 *
 * @extends {Component<Refs>}
 */
export class PostcodeDrawerComponent extends Component {
  connectedCallback() {
    try {
      super.connectedCallback();
      this.setupEventListeners();
      this.storeOriginalButtonText();
      logMessage('log', 'PostcodeDrawerComponent connected successfully');
    } catch (error) {
      logMessage('error', 'Error in PostcodeDrawerComponent connectedCallback', error);
    }
  }

  // ===== EVENT LISTENERS =====
  
  setupEventListeners() {
    try {
      const form = this.querySelector('form');
      if (form) {
        form.addEventListener('submit', (e) => this.handleSubmit(e));
        logMessage('log', 'Form submit event listener added');
      } else {
        logMessage('warn', 'No form found for event listener setup');
      }

      const inputs = this.querySelectorAll('input[type="text"]');
      inputs.forEach(input => {
        input.addEventListener('input', () => this.hidePostcodeError());
      });
      logMessage('log', `Added input event listeners to ${inputs.length} text inputs`);

      this.setupLettersInputUppercase();

      const closeButton = this.refs.closeButton;
      if (closeButton) {
        closeButton.addEventListener('click', (e) => {
          this.close();
        });
      }
    } catch (error) {
      logMessage('error', 'Error setting up event listeners', error);
    }
  }

  setupLettersInputUppercase() {
    try {
      const lettersInput = this.querySelector('[name="postcode_letters"]');
      if (lettersInput) {
        lettersInput.addEventListener('input', (e) => {
          const target = /** @type {HTMLInputElement} */ (e.target);
          target.value = target.value.toUpperCase();
        });
        logMessage('log', 'Letters input uppercase event listener added');
      } else {
        logMessage('warn', 'Letters input not found for uppercase setup');
      }
    } catch (error) {
      logMessage('error', 'Error setting up letters input uppercase', error);
    }
  }

  // ===== INITIALIZATION =====

  storeOriginalButtonText() {
    try {
      const submitButton = this.querySelector('#postcode-submit-button');
      const buttonTextSpan = submitButton?.querySelector('.postcode-submit-button-text');
      
      if (buttonTextSpan?.textContent) {
        this.dataset[DATASET_KEYS.ORIGINAL_BUTTON_TEXT] = buttonTextSpan.textContent;
        logMessage('log', 'Stored original button text:', buttonTextSpan.textContent);
      } else {
        logMessage('warn', 'Could not find button text span for storing original text');
      }
      
      // Store error button text from section settings
      const errorButtonText = this.dataset.errorButtonText || DEFAULT_TEXTS.ERROR_BUTTON;
      this.dataset[DATASET_KEYS.ERROR_BUTTON_TEXT] = errorButtonText;
      logMessage('log', 'Stored error button text:', errorButtonText);
    } catch (error) {
      logMessage('error', 'Error storing original button text', error);
    }
  }

  // ===== AUTO-SUBMISSION LOGIC =====

  async checkAndAutoSubmitPostalCode() {
    try {
      const { digitsInput, lettersInput } = this.getPostcodeInputs();
      if (!digitsInput || !lettersInput) {
        logMessage('warn', 'Postcode inputs not found for auto-submission check');
        return;
      }
      
      const digitsValue = digitsInput.value;
      const lettersValue = lettersInput.value;
      
      if (!digitsValue || !lettersValue) {
        logMessage('log', 'Postcode inputs empty, skipping auto-submission check');
        return;
      }
      
      const formPostcode = this.formatPostcode(digitsValue, lettersValue);
      const cartPostcode = await DeliveryCartAttributes.getCartAttribute('delivery_postcode');
      
      logMessage('log', 'Form postal code:', formPostcode);
      logMessage('log', 'Cart postal code:', cartPostcode);
      
      // Only auto-submit if this is the first time opening the drawer and postal codes don't match
      // Don't auto-submit if the drawer was opened manually (user wants to change postal code)
      const isManualOpen = this.getDatasetFlag(DATASET_KEYS.MANUAL_SUBMISSION) || 
                          this.getDatasetFlag(DATASET_KEYS.JUST_SUBMITTED) ||
                          this.dataset.isManualOpen === 'true';
      
      if (formPostcode !== cartPostcode && !isManualOpen) {
        logMessage('log', 'Auto-submitting form to update cart');
        
        this.setDatasetFlag(DATASET_KEYS.AUTO_SUBMISSION, true);
        this.submitForm();
      } else if (isManualOpen) {
        logMessage('log', 'Manual open detected, skipping auto-submission');
      } else {
        logMessage('log', 'Postal codes match, skipping auto-submission');
      }
    } catch (error) {
      logMessage('error', 'Error in checkAndAutoSubmitPostalCode', error);
    }
  }

  // ===== FORM SUBMISSION =====

  /**
   * @param {Event} e - The submit event
   */
  async handleSubmit(e) {
    try {
      e.preventDefault();
      
      const isAutoSubmission = this.getDatasetFlag(DATASET_KEYS.AUTO_SUBMISSION);
      logMessage('log', 'Postal code form submission - isAutoSubmission:', isAutoSubmission);
      
      if (!isAutoSubmission) {
        this.setDatasetFlag(DATASET_KEYS.MANUAL_SUBMISSION, true);
      }
      
      this.clearDatasetFlag(DATASET_KEYS.AUTO_SUBMISSION);
      
      const { digitsInput, lettersInput } = this.getPostcodeInputs();
      if (!digitsInput || !lettersInput) {
        logMessage('warn', 'Postcode inputs not found for form submission');
        return;
      }
      
      const postcode = this.validateAndFormatPostcode(digitsInput, lettersInput);
      if (!postcode) {
        logMessage('warn', 'Postcode validation failed');
        return;
      }
      
      const cartData = await DeliveryCartAttributes.getCartData();
      if(!cartData) {
        logMessage('error', 'No cart data available');
        return;
      }

      // Check if postal code has actually changed before proceeding
      const cartPostcode = await DeliveryCartAttributes.getCartAttribute('delivery_postcode', cartData);
      const normalizedCartPostcode = Array.isArray(cartPostcode) ? cartPostcode[0] || '' : cartPostcode;
      const selectedDeliveryDay = await DeliveryCartAttributes.getCartAttribute('selected_delivery_time', cartData);
      if (postcode === normalizedCartPostcode && selectedDeliveryDay) {
        logMessage('log', 'Postal code unchanged, skipping submission');
        this.close(); // Close drawer since no change is needed
        return;
      }

      if(postcode === normalizedCartPostcode) {
        logMessage('log', 'Postal code unchanged, but delivery moment missing, moving to delivery times drawer');
        this.handleManualSubmission();
        return;
      }
      
      const isValidRegion = /** @type {any} */ (window).PostalCodeValidator.validatePostcodeRegion(postcode);
      if (!isValidRegion) {
        logMessage('warn', 'Postcode region validation failed:', postcode);
        this.showPostcodeError();
        this.addErrorStyling(digitsInput, lettersInput);
        return;
      }
      
      await this.processValidPostcode(normalizedCartPostcode, postcode, isAutoSubmission);
    } catch (error) {
      logMessage('error', 'Error in handleSubmit', error);
    }
  }

  /**
   * @param {HTMLInputElement} digitsInput - The digits input element
   * @param {HTMLInputElement} lettersInput - The letters input element
   * @returns {string|null} - The formatted postcode or null if invalid
   */
  validateAndFormatPostcode(digitsInput, lettersInput) {
    try {
      const digits = digitsInput.value.trim();
      const letters = lettersInput.value.trim().toUpperCase();
      
      logMessage('log', `Validating postcode: ${digits} ${letters}`);
      
      if (digits.length !== POSTCODE_FORMAT.DIGITS_LENGTH || letters.length !== POSTCODE_FORMAT.LETTERS_LENGTH) {
        logMessage('warn', 'Postcode format validation failed:', { digits, letters, expectedDigits: POSTCODE_FORMAT.DIGITS_LENGTH, expectedLetters: POSTCODE_FORMAT.LETTERS_LENGTH });
        alert(DEFAULT_TEXTS.VALIDATION_ERROR);
        this.addErrorStyling(digitsInput, lettersInput);
        return null;
      }
      
      const formattedPostcode = this.formatPostcode(digits, letters);
      logMessage('log', 'Postcode validation successful:', formattedPostcode);
      return formattedPostcode;
    } catch (error) {
      logMessage('error', 'Error validating postcode', error);
      return null;
    }
  }

  /**
   * @param {string} normalizedCurrentPostalCode - The current postal code
   * @param {string} postcode - The validated postcode
   * @param {boolean} isAutoSubmission - Whether this is an auto-submission
   */
  async processValidPostcode(normalizedCurrentPostalCode, postcode, isAutoSubmission) {
    try {
      logMessage('log', 'Processing new postcode:', postcode);
      
      this.clearErrorState();
      
      let combinedDrawerSections = [];
      if(this.closest('custom-drawer-component')?.parentElement?.getAttribute('data-section-id')){
        combinedDrawerSections.push(this.closest('custom-drawer-component')?.parentElement?.getAttribute('data-section-id') || "");
      }

      const success = await DeliveryCartAttributes.setPostalCodeAndClearDelivery(postcode, combinedDrawerSections);
      
      if (!success) {
        logMessage('error', 'Failed to update postal code in cart');
        alert(DEFAULT_TEXTS.UPDATE_ERROR);
        return;
      }
      
      logMessage('log', 'Successfully updated postal code in cart');
      
      await this.handleSuccessfulPostcodeUpdate(postcode, normalizedCurrentPostalCode, isAutoSubmission);
    } catch (error) {
      logMessage('error', 'Error processing valid postcode', error);
    }
  }

  /**
   * @param {string} postcode - The validated postcode
   * @param {string} oldPostalCode - The previous postal code
   * @param {boolean} isAutoSubmission - Whether this is an auto-submission
   */
  async handleSuccessfulPostcodeUpdate(postcode, oldPostalCode, isAutoSubmission) {
    try {
      logMessage('log', 'Handling successful postcode update:', { postcode, oldPostalCode, isAutoSubmission });
      
      // Notify delivery manager of postal code change with both old and new values
      document.dispatchEvent(new CustomEvent(DeliveryEvents.POSTAL_CODE_CHANGED, {
        detail: { 
          postalCode: postcode,
          oldPostalCode: oldPostalCode
        }
      }));
      
      logMessage('log', 'Dispatched POSTAL_CODE_CHANGED event');
      
      // Close postcode drawer and update display
      // Do not close postcode drawer, because it will be hidden when delivery drawer is opened
      // this.close();

      await updateDeliveryDisplay(postcode);
      
      logMessage('log', 'Updated delivery display');
      
      // Handle delivery times drawer based on submission type
      if (!isAutoSubmission) {
        this.handleManualSubmission();
      } else {
        logMessage('log', 'Auto-submission - skipping delivery times drawer');
      }
    } catch (error) {
      logMessage('error', 'Error handling successful postcode update', error);
    }
  }

  handleManualSubmission() {
    try {
      logMessage('log', 'Manual submission - showing delivery times drawer');
      
      this.setDatasetFlag(DATASET_KEYS.JUST_SUBMITTED, true);
      this.clearDatasetFlag(DATASET_KEYS.MANUAL_SUBMISSION);
      
      // Clear the justSubmitted flag after a delay
      setTimeout(() => {
        this.clearDatasetFlag(DATASET_KEYS.JUST_SUBMITTED);
      }, 2000);

      // Open the combined drawers -> delivery times drawer
      /** @type {any} */ (window).CombinedDrawers.openDelivery();

    } catch (error) {
      logMessage('error', 'Error in handleManualSubmission', error);
    }
  }

  // ===== ERROR HANDLING =====

  clearErrorState() {
    this.hidePostcodeError();
    const { digitsInput, lettersInput } = this.getPostcodeInputs();
    if (digitsInput && lettersInput) {
      this.removeErrorStyling(digitsInput, lettersInput);
    }
  }

  /**
   * @param {HTMLInputElement} digitsInput - The digits input element
   * @param {HTMLInputElement} lettersInput - The letters input element
   */
  addErrorStyling(digitsInput, lettersInput) {
    digitsInput.classList.add('postalcode-input--error');
    lettersInput.classList.add('postalcode-input--error');
    this.updateButtonText(this.dataset[DATASET_KEYS.ERROR_BUTTON_TEXT]);
  }

  /**
   * @param {HTMLInputElement} digitsInput - The digits input element
   * @param {HTMLInputElement} lettersInput - The letters input element
   */
  removeErrorStyling(digitsInput, lettersInput) {
    digitsInput.classList.remove('postalcode-input--error');
    lettersInput.classList.remove('postalcode-input--error');
    this.updateButtonText(this.dataset[DATASET_KEYS.ORIGINAL_BUTTON_TEXT]);
  }

  showPostcodeError() {
    const errorMessage = this.querySelector('#postcode-error-message');
    if (errorMessage) {
      /** @type {HTMLElement} */ (errorMessage).style.display = 'block';
    }
  }

  hidePostcodeError() {
    const errorMessage = this.querySelector('#postcode-error-message');
    if (errorMessage) {
      /** @type {HTMLElement} */ (errorMessage).style.display = 'none';
    }
  }

  // ===== UTILITY METHODS =====

  getPostcodeInputs() {
    const digitsInput = this.querySelector('[name="postcode_digits"]');
    const lettersInput = this.querySelector('[name="postcode_letters"]');
    return {
      digitsInput: /** @type {HTMLInputElement | null} */ (digitsInput),
      lettersInput: /** @type {HTMLInputElement | null} */ (lettersInput)
    };
  }

  /**
   * @param {string} digits - The digits part of the postcode
   * @param {string} letters - The letters part of the postcode
   * @returns {string} - The formatted postcode
   */
  formatPostcode(digits, letters) {
    return `${digits} ${letters}`;
  }

  /**
   * @param {any} text - The text to set on the button
   */
  updateButtonText(text) {
    const submitButton = this.querySelector('#postcode-submit-button');
    const buttonTextSpan = submitButton?.querySelector('.postcode-submit-button-text');
    if (buttonTextSpan) {
      buttonTextSpan.textContent = text || DEFAULT_TEXTS.ORIGINAL_BUTTON;
    }
  }

  submitForm() {
    const form = this.querySelector('form');
    if (form) {
      /** @type {HTMLFormElement} */ (form).requestSubmit();
    }
  }

  // ===== DATASET UTILITIES =====

  /**
   * @param {any} key - The dataset key
   * @param {any} value - The boolean value to set
   */
  setDatasetFlag(key, value) {
    this.dataset[key] = value ? 'true' : 'false';
  }

  /**
   * @param {any} key - The dataset key
   * @returns {boolean} - The boolean value
   */
  getDatasetFlag(key) {
    return this.dataset[key] === 'true';
  }

  /**
   * @param {any} key - The dataset key to clear
   */
  clearDatasetFlag(key) {
    delete this.dataset[key];
  }

  // ===== PUBLIC METHODS =====

  open() {
    try {
      logMessage('log', 'Opening postal code drawer');
      
      // Clear any previous submission flags when opening
      this.clearDatasetFlag(DATASET_KEYS.AUTO_SUBMISSION);
      this.clearDatasetFlag(DATASET_KEYS.MANUAL_SUBMISSION);
      this.clearDatasetFlag(DATASET_KEYS.JUST_SUBMITTED);
      
      // Check for auto-submission only if this is a fresh open
      this.checkAndAutoSubmitPostalCode();

      // Open the combined drawers -> postal code drawer
      /** @type {any} */ (window).CombinedDrawers.openPostalCode();
      
      logMessage('log', 'Postal code drawer opened successfully');
    } catch (error) {
      logMessage('error', 'Error opening postal code drawer', error);
    }
  }

  close() {
    try {
      logMessage('log', 'Closing postal code drawer');
      
      // Clear manual open flag when closing
      delete this.dataset.isManualOpen;

      // Close the combined drawers
      /** @type {any} */ (window).CombinedDrawers.close();
      
      logMessage('log', 'Postal code drawer closed successfully');
    } catch (error) {
      logMessage('error', 'Error closing postal code drawer', error);
    }
  }
}

// Register custom element
if (!customElements.get('postcode-drawer-component')) {
  customElements.define('postcode-drawer-component', PostcodeDrawerComponent);
}

// Export for global access
if (typeof window !== "undefined") {
  /** @type {any} */ (window).PostalCodeDrawer = {
    PostcodeDrawerComponent
  };

  // Backward compatibility
  /** @type {any} */ (window).PostcodeDrawerComponent = PostcodeDrawerComponent;
}
